import React, { useState, useEffect } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Typography,
  Alert
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon, RestartAlt as RestartAltIcon } from '@mui/icons-material';
import config from '../../config/config';

interface User {
  id: number;
  username: string;
  has_logged_in: boolean;
  created_at: string;
}

const UserManagement = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [newUsername, setNewUsername] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${config.api.baseUrl}/auth/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Kunde inte hämta användare');
      }

      const data = await response.json();
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Ett fel uppstod när användarna skulle hämtas');
    }
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setNewUsername('');
    setOpenDialog(true);
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setNewUsername(user.username);
    setOpenDialog(true);
  };

  const handleDeleteUser = async (userId: number) => {
    if (!window.confirm('Är du säker på att du vill ta bort denna användare?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${config.api.baseUrl}/auth/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Kunde inte ta bort användaren');
      }

      setSuccess('Användaren har tagits bort');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      setError('Ett fel uppstod när användaren skulle tas bort');
    }
  };

  const handleSubmit = async () => {
    if (!newUsername.trim()) {
      setError('Användarnamn kan inte vara tomt');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const url = editingUser
        ? `${config.api.baseUrl}/auth/users/${editingUser.id}`
        : `${config.api.baseUrl}/auth/users`;
      
      const response = await fetch(url, {
        method: editingUser ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username: newUsername.trim() })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Ett fel uppstod');
      }

      setSuccess(editingUser ? 'Användaren har uppdaterats' : 'Ny användare har skapats');
      setOpenDialog(false);
      setNewUsername('');
      fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      setError(error instanceof Error ? error.message : 'Ett fel uppstod när användaren skulle sparas');
    }
  };

  const handleResetPassword = async (userId: number) => {
    if (!window.confirm('Är du säker på att du vill återställa lösenordet för denna användare?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${config.api.baseUrl}/auth/users/${userId}/reset-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Kunde inte återställa lösenordet');
      }

      setSuccess('Lösenordet har återställts');
    } catch (error) {
      console.error('Error resetting password:', error);
      setError('Ett fel uppstod när lösenordet skulle återställas');
    }
  };

  return (
    <div>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Användarhantering
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddUser}
          sx={{ mb: 2 }}
        >
          Lägg till ny användare
        </Button>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Användarnamn</TableCell>
                <TableCell>Skapad</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Åtgärder</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>
                    {new Date(user.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {user.has_logged_in ? 'Aktiv' : 'Ej aktiverad'}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => handleEditUser(user)}
                      color="primary"
                      size="small"
                      title="Redigera användare"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleResetPassword(user.id)}
                      color="warning"
                      size="small"
                      title="Återställ lösenord"
                    >
                      <RestartAltIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDeleteUser(user.id)}
                      color="error"
                      size="small"
                      title="Ta bort användare"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>
          {editingUser ? 'Redigera användare' : 'Lägg till ny användare'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Användarnamn"
            type="text"
            fullWidth
            value={newUsername}
            onChange={(e) => setNewUsername(e.target.value)}
            error={!newUsername.trim()}
            helperText={!newUsername.trim() ? 'Användarnamn krävs' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Avbryt</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={!newUsername.trim()}
          >
            Spara
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default UserManagement; 